<template>
    <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
        <!-- Header -->
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-mb-6 tw-gap-4">
            <div>
                <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Company Team</h1>
                <p class="tw-text-gray-600">Meet our talented team members</p>
            </div>
            <div class="tw-flex tw-items-center tw-gap-4">
                <!-- Search Input -->
                <div class="tw-w-full sm:tw-w-80">
                    <FormInput
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search team members..."
                        left-icon="solar:magnifer-linear"
                        class="tw-w-full"
                    />
                </div>
                <!-- Member Count -->
                <div class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-whitespace-nowrap">
                    <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-primary-500" />
                    <span v-if="!loading">{{ totalMembers || filteredTeamMembers.length }} members</span>
                    <span v-else>Loading...</span>
                </div>
            </div>
        </div>

        <!-- Loading State - Team Member Cards Skeleton -->
        <div v-if="loading" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6">
            <div 
                v-for="n in 8" 
                :key="n"
                class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-6 tw-animate-pulse"
            >
                <!-- Avatar Skeleton -->
                <div class="tw-flex tw-justify-center tw-mb-4">
                    <div class="tw-w-20 tw-h-20 tw-bg-gray-200 tw-rounded-full"></div>
                </div>
                
                <!-- Name and Role Skeleton -->
                <div class="tw-text-center tw-mb-4">
                    <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-mx-auto tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-1/2 tw-mx-auto"></div>
                </div>
                
                <!-- Stats Skeleton -->
                <div v-if="false" class="tw-grid tw-grid-cols-2 tw-gap-3">
                    <div v-for="s in 4" :key="s" class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded"></div>
                        <div>
                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-8 tw-mb-1"></div>
                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Actual Team Member Cards -->
        <div v-else class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6">
            <div 
                v-for="member in filteredTeamMembers" 
                :key="member.id"
                class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-6 hover:tw-shadow-lg hover:tw-border-primary-300 tw-transition-all tw-duration-200 tw-cursor-pointer"
                @click="viewMemberProfile(member)"
            >
                <!-- Profile Picture -->
                <div class="tw-flex tw-justify-center tw-mb-4">
                    <div class="tw-relative">
                        <img
                            v-if="member.profile_image || member.avatar"
                            :src="member.profile_image || member.avatar"
                            :alt="member.name"
                            class="tw-w-20 tw-h-20 tw-rounded-full tw-object-cover tw-border-3 tw-border-gray-100"
                        />
                        <div
                            v-else
                            class="tw-w-20 tw-h-20 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-3 tw-border-gray-100"
                        >
                            <icon icon="solar:user-bold-duotone" class="tw-text-3xl tw-text-primary-500" />
                        </div>
                        
                        <!-- Online Status Indicator -->
                        <div
                            v-if="member.is_online || member.isOnline"
                            class="tw-absolute tw-bottom-1 tw-right-1 tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-border-white tw-bg-green-500"
                        ></div>
                    </div>
                </div>

                <!-- Member Info -->
                <div class="tw-text-center tw-mb-4">
                    <h3 class="tw-font-semibold tw-text-gray-900 tw-mb-1 tw-text-lg">
                        {{ member.name }}
                    </h3>
                    <p v-if="member.role" class="tw-text-sm tw-text-gray-600 tw-font-medium">
                        {{ member.role }}
                    </p>
                    <p v-if="member.company_name || member.company?.name" class="tw-text-xs tw-text-gray-500 tw-mt-1 tw-flex tw-items-center tw-justify-center tw-gap-1">
                        <img 
                            v-if="member.company_logo"
                            :src="member.company_logo"
                            :alt="member.company_name || member.company?.name"
                            class="tw-w-3 tw-h-3 tw-rounded tw-object-cover"
                        />
                        <icon v-else icon="solar:buildings-bold-duotone" class="tw-text-xs" />
                        {{ member.company_name || member.company?.name }}
                    </p>
                </div>

                <!-- Stats Grid -->
                <div v-if="false" class="tw-grid tw-grid-cols-2 tw-gap-3">
                    <!-- Projects -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:folder-with-files-bold-duotone" class="tw-text-xl tw-text-blue-600" />
                        <div>
                            <div class="tw-text-sm tw-font-bold tw-text-gray-900">{{ member.stats.projects }}</div>
                            <div class="tw-text-xs tw-text-gray-500">Projects</div>
                        </div>
                    </div>

                    <!-- Meetings -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:calendar-bold-duotone" class="tw-text-xl tw-text-green-600" />
                        <div>
                            <div class="tw-text-sm tw-font-bold tw-text-gray-900">{{ member.stats.meetings }}</div>
                            <div class="tw-text-xs tw-text-gray-500">Meetings</div>
                        </div>
                    </div>

                    <!-- Messages -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:chat-round-bold-duotone" class="tw-text-xl tw-text-purple-600" />
                        <div>
                            <div class="tw-text-sm tw-font-bold tw-text-gray-900">{{ member.stats.messages }}</div>
                            <div class="tw-text-xs tw-text-gray-500">Messages</div>
                        </div>
                    </div>

                    <!-- Tasks -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:checklist-bold-duotone" class="tw-text-xl tw-text-orange-600" />
                        <div>
                            <div class="tw-text-sm tw-font-bold tw-text-gray-900">{{ member.stats.tasks }}</div>
                            <div class="tw-text-xs tw-text-gray-500">Tasks</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State (when no loading and no members) -->
        <div v-if="!loading && filteredTeamMembers.length === 0" class="tw-text-center tw-py-12">
            <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
                {{ searchQuery ? 'No members found matching your search' : 'No team members found' }}
            </h3>
            <p class="tw-text-gray-500">
                {{ searchQuery ? 'Try adjusting your search terms or clear the search to see all members.' : 'Add team members to see them here.' }}
            </p>
            <button 
                v-if="searchQuery" 
                @click="searchQuery = ''"
                class="tw-mt-4 tw-px-4 tw-py-2 tw-bg-primary-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-primary-700 tw-transition-colors"
            >
                Clear Search
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import useAlert from '@/composables/useAlert'
import FormInput from '@/components/form/FormInput.vue'
import myCompanyApi from '@/api/my-company'

const router = useRouter()

// Loading state
const loading = ref(true)

// Search functionality
const searchQuery = ref('')

// Team members data
const teamMembers = ref([])
const totalMembers = ref(0)

// Computed property for filtered team members
const filteredTeamMembers = computed(() => {
    // If API handles search, return all members
    // Otherwise filter client-side
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase().trim()
        return teamMembers.value.filter(member => 
            member.name?.toLowerCase().includes(query) ||
            member.role?.toLowerCase().includes(query) ||
            member.email?.toLowerCase().includes(query) ||
            member.company_name?.toLowerCase().includes(query)
        )
    }
    return teamMembers.value
})

// Methods
const viewMemberProfile = (member) => {
    if (member.id) {
        router.push({
            name: 'shared.community-member',
            params: { id: member.id }
        })
    }
}

// Load team members from API
const loadTeamMembers = async () => {
    try {
        loading.value = true
        
        const params = {}
        
        // Add search query if provided
        if (searchQuery.value && searchQuery.value.trim()) {
            params.search = searchQuery.value.trim()
        }

        const response = await myCompanyApi.getMembers(params)
        const responseData = response.data

        // Handle the API response structure
        if (responseData?.data?.members && Array.isArray(responseData.data.members)) {
            // Map members from API response
            teamMembers.value = responseData.data.members.map(member => ({
                ...member,
                id: member.id,
                name: member.name,
                email: member.email,
                profile_image: member.profile_image,
                role: member.role,
                company_name: member.company_name,
                company_logo: member.company_logo,
                company: member.company,
                is_online: member.is_online
            }))
            
            // Update total count
            totalMembers.value = responseData.data.total || teamMembers.value.length
        } else if (responseData?.data && Array.isArray(responseData.data)) {
            // Fallback: if data is directly an array
            teamMembers.value = responseData.data.map(member => ({
                ...member,
                id: member.id,
                name: member.name,
                email: member.email,
                profile_image: member.profile_image,
                role: member.role,
                company_name: member.company_name,
                company_logo: member.company_logo
            }))
            totalMembers.value = responseData.total || teamMembers.value.length
        } else if (Array.isArray(responseData)) {
            // Simple array response
            teamMembers.value = responseData.map(member => ({
                ...member,
                id: member.id,
                name: member.name,
                email: member.email,
                profile_image: member.profile_image,
                role: member.role,
                company_name: member.company_name,
                company_logo: member.company_logo
            }))
            totalMembers.value = responseData.length
        } else {
            teamMembers.value = []
            totalMembers.value = 0
        }
    } catch (error) {
        console.error('Error loading team members:', error)
        useAlert('Failed to load team members', 'danger')
        teamMembers.value = []
        totalMembers.value = 0
    } finally {
        loading.value = false
    }
}

// Watch for search query changes (debounced)
let searchTimeout = null
watch(searchQuery, () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        loadTeamMembers()
    }, 500)
})

// Initialize component
onMounted(() => {
    loadTeamMembers()
})
</script>

<style scoped>
/* Custom hover effects for cards */
.tw-cursor-pointer:hover {
    transform: translateY(-2px);
}
</style>